import type { FC } from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { ActionIcon, Box, Menu } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { useDisclosure } from '@mantine/hooks';

import { EllipsisVertical, Plus } from 'lucide-react';

import type {
    TProject,
    TProjectData,
    TProjectImageData
} from '@newo/api/gateway';
import Drawer from '@/components/Drawer';
import ProjectForm from '@/pages/ProjectsLegacy/components/ProjectForm';
import ProjectImageForm from '@/pages/Projects/components/ProjectImageForm';
import Header from '@/components/Layout/components/Header';
import ProjectReadmeModal from '@/pages/Projects/components/ProjectReadmeModal';

import { useProjects } from '@/pages/ProjectsLegacy/ProjectsLegacy.hooks';
import { useProjectsLegacyColumns } from '@/pages/ProjectsLegacy/ProjectsLegacy.columns';
import { PageContainer } from '@/components/PageContainer/PageContainer.tsx';

const Projects: FC = () => {
    const [selectedProject, setSelectedProject] = useState<
        TProject | undefined
    >();
    const [selectedProjectForImage, setSelectedProjectForImage] = useState<
        TProject | undefined
    >();
    const [readmeProject, setReadmeProject] = useState<TProject | undefined>();
    const [isDrawerOpened, handlers] = useDisclosure(false);

    const {
        projects,
        isProjectsLoading,
        onProjectCreate,
        onProjectDelete,
        onProjectUpdate,
        onProjectImageCreate,
        onProjectForceUpdate
    } = useProjects();

    const handleProjectDrawerOpen = (project?: TProject) => {
        setSelectedProject(project);
        handlers.open();
    };

    const handleProjectDrawerClose = () => {
        setSelectedProject(undefined);
        handlers.close();
    };

    const handleReadmeClose = () => {
        setReadmeProject(undefined);
    };

    const handleReadmeEdit = () => {
        if (!readmeProject) {
            return;
        }

        setReadmeProject(undefined);
        handleProjectDrawerOpen(readmeProject);
    };

    const handleProjectImageDrawerOpen = (project?: TProject) => {
        setSelectedProjectForImage(project);
    };

    const handleProjectImageDrawerClose = () => {
        setSelectedProjectForImage(undefined);
    };

    const handleProjectImageSubmit = async (data: TProjectImageData) => {
        await onProjectImageCreate({
            data,
            projectId: selectedProjectForImage?.id ?? ''
        });
        handleProjectImageDrawerClose();
    };

    const handleForceUpdate = async (project: TProject) => {
        await onProjectForceUpdate({ projectId: project.id });
    };

    const handleSubmit = async (data: TProjectData, projectId?: string) => {
        await (projectId
            ? onProjectUpdate({ data, projectId })
            : onProjectCreate(data));
        handleProjectDrawerClose();
    };

    const columns = useProjectsLegacyColumns({
        onDeleteProject: (project) => {
            void onProjectDelete(project);
        },
        onEditProject: handleProjectDrawerOpen,
        onForceUpdateProject: (project) => {
            void handleForceUpdate(project);
        },
        onMakeProjectImage: handleProjectImageDrawerOpen,
        onReadmeOpen: setReadmeProject
    });

    return (
        <Box className="flex flex-col h-full">
            <Helmet>
                <title>NEWO - Projects</title>
            </Helmet>
            <Header isSearchVisible breadcrumbs={[{ label: 'Projects' }]}>
                <Menu>
                    <Menu.Target>
                        <ActionIcon variant="transparent">
                            <EllipsisVertical />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            leftSection={<Plus />}
                            onClick={() => {
                                handleProjectDrawerOpen();
                            }}
                        >
                            Create New Project
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Header>
            <PageContainer className="flex-1 min-h-0 flex flex-col pt-6">
                <DataTable
                    highlightOnHover
                    pinLastColumn
                    columns={columns}
                    fetching={isProjectsLoading}
                    height="85vh"
                    idAccessor="id"
                    loaderBackgroundBlur={1}
                    loaderColor="dark"
                    records={projects}
                    scrollAreaProps={{
                        scrollbarSize: 10,
                        type: 'always'
                    }}
                    verticalAlign="top"
                />
            </PageContainer>
            <Drawer
                name="project-form"
                opened={isDrawerOpened}
                position="right"
                size="md"
                title={selectedProject ? 'Edit Project' : 'Create Project'}
                onClose={handleProjectDrawerClose}
            >
                <ProjectForm
                    project={selectedProject}
                    onClose={handleProjectDrawerClose}
                    onSubmit={handleSubmit}
                />
            </Drawer>
            <Drawer
                name="project-image"
                opened={!!selectedProjectForImage}
                position="right"
                size="sm"
                title="Make Project Image"
                onClose={handleProjectImageDrawerClose}
            >
                <ProjectImageForm
                    project={selectedProjectForImage}
                    onCancel={handleProjectImageDrawerClose}
                    onSubmit={handleProjectImageSubmit}
                />
            </Drawer>
            <ProjectReadmeModal
                project={readmeProject}
                onClose={handleReadmeClose}
                onEdit={handleReadmeEdit}
            />
        </Box>
    );
};

export default Projects;
